import { useState } from 'react';
import { useRequest } from 'ahooks';

import {
  ButtonWithArrowRight,
  Footer,
  Title,
  InputFileButton,
  TransshipmentSendFilePreview,
  LoadingSpinner,
  Text,
  Toast,
  Button,
} from '~components';
import NumberedList, { NumberedListItem } from '~components/dumbs/NumberedList';
import { useTransshipment } from '~contexts/TranshipmentContext';
import { Response, ParamsFile } from '~types/getUserData';

import { Representative, Spouse } from '~services';
import { formatFileToBase64 } from '~utils/formatters';

import * as Styled from './CnhTutorialUploadFile.styles';
import * as amplitude from '@amplitude/analytics-browser';

type TPageMode =
  | 'INITIAL'
  | 'FILESELECTED'
  | 'SENDING'
  | 'DONESENDING'
  | 'BACKENDERROR';

const fileMaxSizeKB = process.env.REACT_APP_MAX_FILE_SIZE || 4507904;

function CnhTutorialUploadFile({
  onClickOpenCamera,
}: {
  onClickOpenCamera: () => void;
}) {
  const transshipment = useTransshipment();
  const [file, setFile] = useState<File>();
  const [pageMode, setPageMode] = useState<TPageMode>('INITIAL');

  const [isToastVisible, setIsToastVisible] = useState(false);
  const [toastErrorMessage, setToastMessage] = useState<string>();

  const addDocumentService =
    transshipment.type === 'spouse'
      ? Spouse.addDocument
      : Representative.addDocument;

  const { runAsync: addDocument } = useRequest<Response, ParamsFile>(
    addDocumentService,
    {
      manual: true,
      onError: () => setPageMode('BACKENDERROR'),
      onSuccess: () => {
        if (transshipment.type === 'representative') {
          amplitude.track({
            event_type: 'documentation_id_sent',
            event_properties: {
              CPF_representatives: transshipment.data.cpf,
            },
          });
        }
        setPageMode('DONESENDING');
      },
    }
  );

  async function sendFile() {
    setPageMode('SENDING');

    const base64File = await formatFileToBase64(file);

    await addDocument(transshipment?.id, {
      file: base64File,
      type: 'cnh',
    });
  }

  function handleContinue() {
    transshipment.nextStep();
  }

  function handleFileChange(newFile: File | undefined) {
    if (!newFile) return setPageMode('INITIAL');

    if (!/\.pdf$/.test(newFile.name)) {
      setToastMessage('O arquivo deve ser um PDF');
      setIsToastVisible(true);
      return;
    }

    if (newFile.size > fileMaxSizeKB) {
      setToastMessage(`Arquivo ultrapassa o limite de ${getFileMaxSizeMB()}MB`);
      setIsToastVisible(true);
      return;
    }

    setFile(newFile);
    setPageMode('FILESELECTED');
  }

  function handleTakePicture() {
    onClickOpenCamera();
  }

  function getFileMaxSizeMB() {
    return (fileMaxSizeKB / 1024 / 1024).toFixed(1);
  }

  return (
    <>
      {pageMode === 'INITIAL' && (
        <>
          <Styled.Header>
            <Styled.DocumentPhotoSvg />
            <Title>Frente da CNH</Title>
          </Styled.Header>

          <NumberedList>
            <NumberedListItem>
              Retire o plástico do seu documento
            </NumberedListItem>
            <NumberedListItem>
              Tire as fotos em um local iluminado
            </NumberedListItem>
            <NumberedListItem>
              Certifique-se que os dados estão legíveis na foto
            </NumberedListItem>
          </NumberedList>

          <Styled.LegendText>
            Caso possua, opte por anexar o PDF do seu CNH com até{' '}
            {getFileMaxSizeMB()}MB.
          </Styled.LegendText>

          <Footer
            marginTop="auto"
            leftButton={
              <InputFileButton
                onChange={(file) => handleFileChange(file)}
                accept=".pdf"
              >
                Anexar PDF
              </InputFileButton>
            }
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                onClick={handleTakePicture}
              >
                Tirar foto
              </ButtonWithArrowRight>
            }
          />
        </>
      )}

      {pageMode === 'FILESELECTED' && (
        <>
          <TransshipmentSendFilePreview
            variant="FILESELECTED"
            filename={file.name}
            message={<Text variant="sub400">O PDF da CNH está correto?</Text>}
          />

          <Footer
            marginTop="auto"
            leftButton={
              <Button
                width="100%"
                variant="secondary"
                onClick={() => setPageMode('INITIAL')}
              >
                Não, trocar
              </Button>
            }
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                onClick={sendFile}
              >
                Sim
              </ButtonWithArrowRight>
            }
          />
        </>
      )}

      {pageMode === 'SENDING' && (
        <>
          <TransshipmentSendFilePreview
            variant="SENDING"
            filename={file.name}
            message={<Text variant="sub400">Enviando PDF da CNH</Text>}
          />
          <Styled.EmptyFooter>
            <LoadingSpinner />
          </Styled.EmptyFooter>
        </>
      )}

      {pageMode === 'DONESENDING' && (
        <>
          <TransshipmentSendFilePreview
            variant="DONESENDING"
            filename={file.name}
            message={
              <>
                <Text variant="sub400" color="neutral-strong">
                  <Styled.CheckIcon /> Pdf da CNH enviado
                </Text>
              </>
            }
          />
          <Footer
            marginTop="auto"
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                onClick={() => handleContinue()}
              >
                Continuar
              </ButtonWithArrowRight>
            }
          />
        </>
      )}

      {pageMode === 'BACKENDERROR' && (
        <>
          <TransshipmentSendFilePreview
            variant="BACKENDERROR"
            filename={file.name}
            message="Erro ao enviar o arquivo, envie novamente."
          />
          <Footer
            marginTop="auto"
            rightButton={
              <ButtonWithArrowRight
                width="100%"
                variant="primary"
                onClick={() => setPageMode('INITIAL')}
              >
                Reenviar
              </ButtonWithArrowRight>
            }
          />
        </>
      )}

      <Toast
        isVisible={!!isToastVisible}
        title={toastErrorMessage}
        onDismiss={() => setIsToastVisible(false)}
        variant="error"
      />
    </>
  );
}

export default CnhTutorialUploadFile;
